import React, { useState } from 'react';
import { CloseOutlined, CalendarOutlined, EditOutlined } from '@ant-design/icons';
import { Divider, message, Flex, Popconfirm } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../stores/root-store-context';
import { Link } from 'react-router-dom';
import { AddExperience } from '../../pages/Users/AddExperience';
import { Experience } from '../../models/Experience';

interface ExperienceBlockProps {
  experienceList: Experience[];
  canRemove?: boolean;
  removeExperience: (experience: Experience) => Promise<void>;
  updatePage: () => void;
}

const ExperienceItem: React.FC<{
  item: Experience;
  canRemove: boolean;
  removeExperience: (experience: Experience) => Promise<void>;
  onEdit: (experience: Experience) => void;
  t: any;
  isCurrentCompany?: boolean;
  projectId?: string;
  tenant?: string;
}> = ({ item, canRemove, removeExperience, t, isCurrentCompany, projectId, tenant, onEdit }) => (
  <div
    style={{
      position: "relative",
      backgroundColor: "#fff",
      padding: "16px",
      borderRadius: "8px",
      border: "1px solid #d4d4d4",
      overflow: "hidden",
    }}
  >
    {canRemove && (
      <Flex align='end' justify='end' style={{ position: "absolute", top: "8px", right: "8px" }} gap={8}>
        <EditOutlined
          style={{
            top: "8px",
            right: "8px",
            cursor: "pointer",
            fontSize: "16px",
          }}
          onClick={async () => {
            onEdit(item);
          }}
        />
        <Popconfirm
          title={t("DeleteItem")}
          okText={t("Yes")}
          cancelText={t("No")}
          onConfirm={async () => await removeExperience(item)}
        >
          <CloseOutlined
            style={{
              top: "8px",
              right: "8px",
              cursor: "pointer",
              color: "#ff4d4f",
              fontSize: "16px",
            }}
          />
        </Popconfirm>

      </Flex>

    )}
    <h3 style={{ marginBottom: "8px", fontWeight: "bold", color: "#2a2a2a", fontSize: "16px" }}>
      {t("Company")} {item.Company}
    </h3>
    <div style={{ marginBottom: "8px", color: "#4a4a4a", fontSize: "14px" }}>
      {t("Project")}: {
        isCurrentCompany && projectId && tenant ? (
          <Link
            to={`/${tenant}/projects/${projectId}`}
            style={{ color: "blue", textDecoration: "underline" }}
          >
            {item.Project}
          </Link>
        ) : item.Project
      }
    </div>
    <div style={{ marginBottom: "8px", color: "#4a4a4a", fontSize: "14px" }}>
      {t("Position")}: {item.Position}
    </div>
    <div style={{ color: "#8c8c8c", fontSize: "14px" }}>
      <CalendarOutlined style={{ marginRight: "8px", color: "#faad14" }} />
      {item.StartDate ? dayjs(item.StartDate).format("YYYY-MM-DD") : ""} - {
        (item.EndDate === "0001-01-01" || item.EndDate === "1901-01-01" || !item.EndDate)
          ? t("PresentTime")
          : dayjs(item.EndDate).format("YYYY-MM-DD")
      }
    </div>
    {item.Description && (
      <>
        <Divider style={{ margin: "12px 0", borderColor: "#e8e8e8" }} />
        <div style={{
          backgroundColor: "#e6f7ff",
          color: "#1890ff",
          padding: "8px 12px",
          borderRadius: "4px",
          fontWeight: "bold",
          fontSize: "14px",
        }}>
          <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 14, fontWeight: 400 }}>
            {item.Description}
          </pre>
        </div>
      </>
    )}
  </div>
);

const ExperienceBlock: React.FC<ExperienceBlockProps> = observer(({ experienceList, canRemove = false, removeExperience, updatePage }) => {
  const { t } = useTranslation();
  const { applicationStore } = useStore();
  const [isModalExperience, setIsModalExperience] = useState(false);
  const [experienceItem, setExperienceItem] = useState<Experience | undefined>();

  if (experienceList.length === 0) {
    return null;
  }

  const currentCompanyExperiences = experienceList.filter(exp =>
    exp.Project && exp.Company === applicationStore.company?.Title
  ).sort((a, b) => dayjs(a.StartDate).isBefore(dayjs(b.StartDate)) ? 1 : -1);

  const otherExperiences = experienceList.filter(exp =>
    !exp.Project || exp.Company !== applicationStore.company?.Title
  ).sort((a, b) => dayjs(a.StartDate).isBefore(dayjs(b.StartDate)) ? 1 : -1);

  return (
    <div style={{
      padding: "20px",
      border: "1px solid #d4d4d4",
      borderRadius: "8px",
      width: "100%",
      marginBottom: "16px",
    }}>
      {currentCompanyExperiences.length > 0 && (
        <>
          <h3 style={{ marginBottom: "20px", color: "#333" }}>
            {t("Experience")} {t("in")} {applicationStore.company?.Title}
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "24px" }}>
            {currentCompanyExperiences.map((item, index) => (
              <ExperienceItem
                key={`current-${index}`}
                item={item}
                canRemove={canRemove}
                removeExperience={removeExperience}
                onEdit={(item: Experience) => {
                  setExperienceItem(item);
                  setIsModalExperience(true);
                }}
                t={t}
                isCurrentCompany={true}
                projectId={item.ProjectId}
                tenant={applicationStore.company?.Tenant}
              />
            ))}
          </div>
        </>
      )}

      {otherExperiences.length > 0 && (
        <>
          <h3 style={{ marginBottom: "20px", color: "#333" }}>
            {t("Experience")} {t("in")} {t("OtherCompanies")}
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {otherExperiences.map((item, index) => (
              <ExperienceItem
                key={`other-${index}`}
                item={item}
                canRemove={canRemove}
                removeExperience={removeExperience}
                onEdit={(item: Experience) => {
                  setExperienceItem(item);
                  setIsModalExperience(true);
                }}
                t={t}
                isCurrentCompany={false}
              />
            ))}
          </div>
        </>
      )}
      <AddExperience isModalOpen={isModalExperience} hideModal={() => {
        setIsModalExperience(false);
        setExperienceItem(undefined);
      }}
        experienceItem={experienceItem}
        fetchExperience={() => {
          updatePage();
        }} />
    </div>
  );
});

export default ExperienceBlock;
